// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTo";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { checkoutReviewAndSubmitStyle } from "@plumberssupply/Overrides/Widgets/CheckoutReviewAndSubmit/checkoutReviewAndSubmit.shared.styles";
import { color, parseBoolean } from "@plumberssupply/XDL/Utilities";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const { cartId } = state.pages.checkoutReviewAndSubmit;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    return {
        cartState,
        shipToState: getShipToState(state, cartState.value?.shipToId),
        pickUpWarehouse: state.context.session.pickUpWarehouse,
    };
};

const mapDispatchToProps = {
    loadShipTo,
};

export interface ExpressCheckoutShippingInformationStyles {
    shippingInformationGridContainer?: GridContainerProps;
    shippingInformationAddressGridItem?: GridItemProps;
    shippingAddressTitle?: TypographyProps;
    shippingAddress?: AddressInfoDisplayStyles;
}

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export const expressCheckoutShippingInformationStyles: ExpressCheckoutShippingInformationStyles = {
    shippingInformationAddressGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    shippingAddressTitle: {
        color: "common.ui11",
        variant: "h5",
        css: css`
            @media print {
                font-size: 12px;
            }
        `,
    },
    shippingInformationGridContainer: {
        gap: 10,
        css: css`
            padding-bottom: 32px;
            margin-bottom: 32px;
            border-bottom: 1px solid ${color("common.ui7")};
        `,
    },
};

const styles = expressCheckoutShippingInformationStyles;

const ExpressCheckoutShippingInformation = ({ cartState, shipToState, pickUpWarehouse }: Props) => {
    useEffect(() => {
        if (cartState.isLoading || !cartState.value) {
            return;
        }

        const cart = cartState.value;
        if (!shipToState.isLoading && !shipToState.value && cart.billToId && cart.shipToId) {
            loadShipTo({ billToId: cart.billToId, shipToId: cart.shipToId });
        }
    }, [cartState]);

    const cart = cartState.value;
    const shipTo = shipToState.value;
    if (!cart || !shipTo) {
        return null;
    }
    const plumbersWithNewForm = parseBoolean(shipTo?.properties["plumbers_WithNewForm"]) || shipTo.isNew;
    return (
        <GridContainer
            {...styles.shippingInformationGridContainer}
            data-test-selector="orderConfirmationShippingInformation"
        >
            <GridItem {...styles.shippingInformationAddressGridItem}>
                <Typography {...checkoutReviewAndSubmitStyle.sectionTitle}>{translate("Addresses")}</Typography>
            </GridItem>

            {cart.fulfillmentMethod === FulfillmentMethod.Ship && (
                <GridItem {...styles.shippingInformationAddressGridItem}>
                    <Typography {...styles.shippingAddressTitle}>{translate("Shipping Address")}</Typography>
                    <AddressInfoDisplay
                        companyName={shipTo.companyName}
                        address1={shipTo.address1}
                        address2={shipTo.address2}
                        address3={shipTo.address3}
                        address4={shipTo.address4}
                        city={shipTo.city}
                        postalCode={shipTo.postalCode}
                        state={shipTo.state ? shipTo.state.abbreviation : undefined}
                        extendedStyles={styles.shippingAddress}
                        {...{
                            customShipping: plumbersWithNewForm,
                            contactName: shipTo.properties.plumbers_ContactName,
                        }}
                    />
                </GridItem>
            )}
            {cart.fulfillmentMethod === FulfillmentMethod.PickUp && pickUpWarehouse && (
                <GridItem {...styles.shippingInformationAddressGridItem}>
                    <Typography {...styles.shippingAddressTitle}>{translate("Pick Up Address")}</Typography>
                    <AddressInfoDisplay
                        {...pickUpWarehouse}
                        companyName={pickUpWarehouse.description || pickUpWarehouse.name}
                        extendedStyles={styles.shippingAddress}
                    />
                </GridItem>
            )}
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ExpressCheckoutShippingInformation),
    definition: {
        group: "Checkout - Review & Submit",
        allowedContexts: ["ExpressCheckoutPage"] as any,
        displayName: "Shipping Information",
    },
};

export default widgetModule;
